import {
	ColumnHeader,
	IColumnHeader,
	IPageRange,
	NoResults,
	PageTurnOptions,
	PagingHelper,
	PagingOptions,
	SearchBox,
	Switch,
	Table,
	ToolMenu,
	ToolMenuContent,
} from '@aon/aon-tws-common-ui';
import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useAppDispatch } from '../../../../appStore';
import ContentConstants from '../../../../contentConstants';
import { ISurveyQuestionEditModel, ITranslationEditItem, IValidationErrorCollection } from '../../../../models';
import { toggleResponseIsRequired, updateSurveyQuestionTranslation } from '../../../../store/SurveyQuestionsAdminSlice';
import { assertNonNullable } from '../../../../utilities/assertions';
import { useSurveyDefaultCultureCode } from '../../../../utilities/hooks';
import { FollowUpQuestionsTableRow } from './FollowUpQuestionsTableRow';
import { QuestionsTableRow } from './QuestionsTableRow';
import { QuestionTranslationsModal } from './QuestionTranslationsModal';

export interface IQuestionsTableProps {
	questions: ISurveyQuestionEditModel[];
	translations: { [surveyQuestionId: number]: ITranslationEditItem[] };
	isSurveyClosed: boolean;
	validations: { errors: IValidationErrorCollection; warnings: IValidationErrorCollection };
}

const ToolMenuContentSwitchWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	height: 36px;
`;

interface IQuestionsTableState {
	columns: IColumnHeader[];
	pageRange: IPageRange;
	searchText: string;
	questions: ISurveyQuestionEditModel[];
	allResponsesAreRequired: boolean;
	translationsModal: {
		isOpen: boolean;
		surveyQuestionId?: number;
	};
}

type DataSourceChangedAction = { type: 'data-source-changed'; dataSource: ISurveyQuestionEditModel[] };
type PaginateAction = { type: 'paginate'; dataSource: ISurveyQuestionEditModel[]; payload: IPageRange };
type SearchAction = { type: 'search'; dataSource: ISurveyQuestionEditModel[]; payload: string };
type OpenTranslationsModalAction = { type: 'open-translations-modal'; payload: number };
type CloseTranslationsModalAction = { type: 'close-translations-modal' };

const reducer = (
	prevState: IQuestionsTableState,
	action: DataSourceChangedAction | PaginateAction | SearchAction | OpenTranslationsModalAction | CloseTranslationsModalAction,
) => {
	const newState = { ...prevState };

	if (action.type === 'open-translations-modal' || action.type === 'close-translations-modal') {
		newState.translationsModal.isOpen = !newState.translationsModal.isOpen;
		newState.translationsModal.surveyQuestionId = action['payload'];
		return newState;
	}

	const { dataSource } = action;
	newState.questions = dataSource;

	switch (action.type) {
		case 'data-source-changed':
			newState.pageRange.total = newState.questions.length;

			if (
				(newState.allResponsesAreRequired && dataSource.every((q) => !q.isRequired)) ||
				(!newState.allResponsesAreRequired && dataSource.every((q) => q.isRequired))
			) {
				newState.allResponsesAreRequired = !newState.allResponsesAreRequired;
			}
			break;
		case 'paginate':
			newState.pageRange = action.payload;
			break;
		case 'search':
			newState.searchText = action.payload;

			if (newState.searchText.length > 0) {
				const regex = new RegExp(newState.searchText, 'i');
				newState.questions = newState.questions.filter(
					(question) => question.code?.search(regex) !== -1 || question.text.search(regex) !== -1,
				);
			}

			newState.pageRange.total = newState.questions.length;
			break;
	}

	const pagingHelper = new PagingHelper<ISurveyQuestionEditModel>();
	newState.questions = pagingHelper.page(newState.questions, newState.pageRange);
	return newState;
};

const initializer = (dataSource: ISurveyQuestionEditModel[]) => {
	const columns = [
		{ label: 'Question ID', field: 'code', sortAscByDefault: false, isSortable: false, tooltipContent: undefined },
		{ label: 'Question', field: 'text', sortAscByDefault: false, isSortable: false, tooltipContent: undefined },
		{ label: 'Response Required', field: 'isRequired', sortAscByDefault: false, isSortable: false, tooltipContent: undefined },
		{ label: 'Follow Up Question', field: 'followUpQuestion', sortAscByDefault: false, isSortable: false, tooltipContent: undefined },
		{ label: 'Translations', field: 'editTranslations', sortAscByDefault: false, isSortable: false, tooltipContent: undefined },
	];

	const allResponsesAreRequired = dataSource.every((q) => q.isRequired);
	const pageRange = { offset: 0, limit: 25, total: dataSource.length };
	const pagingHelper = new PagingHelper<ISurveyQuestionEditModel>();
	return {
		columns: columns,
		pageRange,
		searchText: '',
		questions: pagingHelper.page(dataSource, pageRange),
		allResponsesAreRequired,
		translationsModal: {
			isOpen: false,
		},
	};
};

export const useQuestionsTable = (dataSource: ISurveyQuestionEditModel[]) => {
	const [state, dispatch] = useReducer(reducer, dataSource, initializer);

	const onDataSourceChangedHandler = () => dispatch({ type: 'data-source-changed', dataSource });

	const onPaginateHandler = (pageRange: IPageRange) =>
		dispatch({
			type: 'paginate',
			dataSource,
			payload: pageRange,
		});

	const onSearchHandler = (searchText: string) =>
		dispatch({
			type: 'search',
			dataSource,
			payload: searchText,
		});

	const openTranslationsModal = useCallback(
		(surveyQuestionId: number) => dispatch({ type: 'open-translations-modal', payload: surveyQuestionId }),
		[dispatch],
	);

	const closeTranslationsModal = () => dispatch({ type: 'close-translations-modal' });

	return {
		state,
		onDataSourceChangedHandler,
		onPaginateHandler,
		onSearchHandler,
		openTranslationsModal,
		closeTranslationsModal,
	};
};

export const QuestionsTable = ({ questions, translations, isSurveyClosed, validations }: IQuestionsTableProps) => {
	const dispatch = useAppDispatch();
	const { state, onDataSourceChangedHandler, onPaginateHandler, onSearchHandler, openTranslationsModal, closeTranslationsModal } =
		useQuestionsTable(questions);
	const surveyDefaultCultureCode = useSurveyDefaultCultureCode();

	const mounted = useRef(false);

	useDeepCompareEffect(() => {
		if (mounted.current) {
			onDataSourceChangedHandler();
		}
	}, [questions]);

	useEffect(() => {
		mounted.current = true;

		return () => {
			mounted.current = false;
		};
	}, []);

	const filteredQuestions = state.questions.filter((q) => !q.parentQuestionId);
	const getFollowUpQuestionElement = (questionId: number) => {
		const parentQuestion = state.questions.find((q) => q.surveyQuestionId === questionId);
		const followUpQuestion = state.questions.find((q) => q.parentQuestionId === questionId);
		if (followUpQuestion) {
			assertNonNullable(followUpQuestion.surveyQuestionId);
			assertNonNullable(parentQuestion);
			return (
				<FollowUpQuestionsTableRow
					key={followUpQuestion.surveyQuestionId}
					errors={validations.errors[followUpQuestion.surveyQuestionId]}
					isParentRequired={parentQuestion.isRequired}
					question={followUpQuestion}
					searchText={state.searchText}
					warnings={validations.warnings[followUpQuestion.surveyQuestionId]}
					editTranslations={openTranslationsModal}
				/>
			);
		} else {
			return undefined;
		}
	};

	return (
		<>
			<PagingOptions pageRange={state.pageRange} updatePageRange={onPaginateHandler} />
			<ToolMenu openByDefault={true}>
				<ToolMenuContent type='left'>
					<ToolMenuContentSwitchWrapper>
						<Switch
							text={
								<>
									<b>Select all</b> Response Required
								</>
							}
							checked={state.allResponsesAreRequired}
							disabled={isSurveyClosed}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
								dispatch(
									toggleResponseIsRequired({
										isRequired: event.target.checked,
									}),
								)
							}
						/>
					</ToolMenuContentSwitchWrapper>
				</ToolMenuContent>
				<ToolMenuContent type='right'>
					<SearchBox
						initialSearchText={state.searchText}
						resultsCount={state.questions.length}
						callbackFunc={onSearchHandler}
						inputSize='sm'
					/>
				</ToolMenuContent>
			</ToolMenu>
			<Table responsive={true} lastalign={state.columns.length === 5 ? 'right' : undefined}>
				<ColumnHeader columns={state.columns} />
				<tbody>
					{filteredQuestions.map((question: ISurveyQuestionEditModel) => {
						assertNonNullable(question.surveyQuestionId);
						return (
							<QuestionsTableRow
								key={question.surveyQuestionId}
								columns={state.columns}
								errors={validations.errors[question.surveyQuestionId]}
								warnings={validations.warnings[question.surveyQuestionId]}
								editTranslations={openTranslationsModal}
								question={question}
								searchText={state.searchText}
								isSurveyClosed={isSurveyClosed}
								followUpQuestion={getFollowUpQuestionElement(question.surveyQuestionId)}
							/>
						);
					})}
				</tbody>
			</Table>
			{state.questions.length === 0 && (
				<NoResults title={ContentConstants.SEARCH_NO_RESULTS_TITLE} message={ContentConstants.SEARCH_NO_RESULTS_MESSAGE} />
			)}
			<PageTurnOptions pageRange={state.pageRange} updatePageRange={onPaginateHandler} />
			{state.translationsModal.surveyQuestionId !== undefined && (
				<QuestionTranslationsModal
					key={`${state.translationsModal.surveyQuestionId}-translations-modal`}
					isOpen={state.translationsModal.isOpen}
					translations={translations[state.translationsModal.surveyQuestionId]}
					onCancel={closeTranslationsModal}
					onSubmit={(updatedTranslations) => {
						updatedTranslations.forEach((updatedTranslation) =>
							dispatch(
								updateSurveyQuestionTranslation({
									surveyQuestionId: state.translationsModal.surveyQuestionId,
									cultureCode: updatedTranslation.cultureCode,
									textValue: updatedTranslation.textValue,
									defaultCultureCode: surveyDefaultCultureCode,
								}),
							),
						);
						closeTranslationsModal();
					}}
				/>
			)}
		</>
	);
};
